import React from "react";
import { motion, useMotionValue, useTransform, animate, AnimationPlaybackControls } from "framer-motion";
import useStoreRoom from "store/useStoreRoom";

type CircleProgressProps = {
    turnEndsAt: number | null,
    turnDuration: number,
}

const CIRCUMFERENCE = 2 * Math.PI * 29;
const RESYNC_THRESHOLD_MS = 150;

const CircleProgress = React.memo(({ turnEndsAt, turnDuration }: CircleProgressProps) => {
    const clockOffset = useStoreRoom(state => state.clockOffset);
    const progress = useMotionValue(CIRCUMFERENCE);
    const stroke = useTransform(progress, [CIRCUMFERENCE * 0.1, CIRCUMFERENCE * 0.6], ["#FF3333", "#FFD433"]);
    const controls = React.useRef<AnimationPlaybackControls | null>(null);
    const syncedDeadline = React.useRef<number | null>(null);
    const deadline = turnEndsAt === null ? null : turnEndsAt - clockOffset;

    React.useEffect(() => {
        if(deadline === null || turnDuration <= 0) {
            return;
        }

        if(controls.current && syncedDeadline.current !== null && Math.abs(syncedDeadline.current - deadline) < RESYNC_THRESHOLD_MS) {
            return;
        }

        syncedDeadline.current = deadline;
        controls.current?.stop();

        const totalMs = turnDuration * 1000;
        const remainingMs = Math.max(0, deadline - Date.now());
        const runningMs = Math.min(remainingMs, totalMs);

        progress.set((runningMs / totalMs) * CIRCUMFERENCE);
        controls.current = animate(progress, 0, {
            duration: runningMs / 1000,
            delay: (remainingMs - runningMs) / 1000,
            ease: "linear",
        });
    }, [deadline, turnDuration]);

    React.useEffect(() => () => controls.current?.stop(), []);

    return (
        <svg className="absolute -rotate-90 w-[115%] h-[115%] -left-[7.5%] -top-[7.5%] z-20 pointer-events-none" viewBox="0 0 64 64">
            <circle className="stroke-white/30 stroke-[4]" fill="none" r="29" cx="32" cy="32" />
            <motion.circle
                className="stroke-[4]"
                strokeLinecap="round"
                fill="none"
                r="29"
                cx="32"
                cy="32"
                style={{ stroke, strokeDashoffset: progress }}
                strokeDasharray={CIRCUMFERENCE}
            />
        </svg>
    );
});

export default CircleProgress;
